import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { requireAuth } from "~/lib/auth-guard";
import { RouteErrorBoundary } from "~/components/RouteErrorBoundary";
import * as m from "~/paraglide/messages";

export const Route = createFileRoute("/help")({
  beforeLoad: async () => {
    await requireAuth();
  },
  component: HelpPage,
  errorComponent: RouteErrorBoundary,
});

const faqSections = [
  {
    category: "Getting started",
    items: [
      { title: () => m.help_getting_started(), content: () => m.help_getting_started_content() },
    ],
  },
  {
    category: "Resume & Cover Letter",
    items: [
      { title: () => m.help_resume_builder(), content: () => m.help_resume_builder_content() },
    ],
  },
  {
    category: "Jobs",
    items: [
      { title: () => m.help_job_matching(), content: () => m.help_job_matching_content() },
    ],
  },
  {
    category: "Account & Billing",
    items: [
      { title: () => m.help_account_billing(), content: () => m.help_account_billing_content() },
    ],
  },
] as const;

function SearchIcon() {
  return (
    <svg
      className="h-4 w-4 shrink-0"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.35-4.35" />
    </svg>
  );
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      className="h-4 w-4 shrink-0 transition-transform duration-200"
      style={{ transform: open ? "rotate(90deg)" : "rotate(0deg)" }}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 18l6-6-6-6" />
    </svg>
  );
}

function HelpPage() {
  const [query, setQuery] = useState("");
  const [openIndex, setOpenIndex] = useState<string | null>(null);

  const allItems = faqSections.flatMap((section) =>
    section.items.map((item) => ({ ...item, category: section.category })),
  );

  const filtered = query.trim()
    ? allItems.filter(
        (item) =>
          item.title().toLowerCase().includes(query.toLowerCase()) ||
          item.content().toLowerCase().includes(query.toLowerCase()),
      )
    : null;

  function toggle(key: string) {
    setOpenIndex((prev) => (prev === key ? null : key));
  }

  return (
    <div className="mx-auto max-w-2xl p-6 sm:p-8">
      {/* Attio page header */}
      <div className="animate-fade-up">
        <h1
          className="font-display text-[22px] font-semibold tracking-tight"
          style={{ color: "var(--text)", fontFamily: "var(--font-display, inherit)" }}
        >
          {m.help_title()}
        </h1>
        <p
          className="mt-1 text-sm"
          style={{ color: "var(--text-muted)" }}
        >
          Find answers to common questions about Alchema.
        </p>
      </div>

      {/* Search bar */}
      <div
        className="mt-6 flex items-center gap-2.5 rounded-[10px] border px-3.5 py-2.5"
        style={{
          backgroundColor: "var(--surface-raised)",
          borderColor: "var(--border-subtle)",
          color: "var(--text-muted)",
        }}
      >
        <SearchIcon />
        <input
          type="search"
          placeholder="Search articles…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-full bg-transparent text-sm outline-none placeholder:text-[var(--text-muted)]"
          style={{ color: "var(--text)" }}
        />
      </div>

      {/* Results / FAQ sections */}
      <div className="mt-8">
        {filtered ? (
          /* Search results mode */
          <div>
            <p className="section-label mb-4">{filtered.length} result{filtered.length !== 1 ? "s" : ""}</p>
            {filtered.length === 0 ? (
              <p className="text-sm" style={{ color: "var(--text-muted)" }}>
                No articles match your search.
              </p>
            ) : (
              <div
                className="rounded-[10px] border overflow-hidden"
                style={{ borderColor: "var(--border-subtle)" }}
              >
                {filtered.map((item, i) => {
                  const key = `search-${i}`;
                  const isOpen = openIndex === key;
                  return (
                    <div
                      key={key}
                      style={{
                        borderBottom:
                          i < filtered.length - 1
                            ? "1px solid var(--border-subtle)"
                            : "none",
                      }}
                    >
                      <button
                        type="button"
                        className="flex w-full cursor-pointer items-center justify-between px-4 py-3 text-left"
                        onClick={() => toggle(key)}
                        aria-expanded={isOpen}
                      >
                        <div>
                          <p
                            className="text-sm font-medium"
                            style={{ color: "var(--text)" }}
                          >
                            {item.title()}
                          </p>
                          <p className="mt-0.5 text-[11px] uppercase tracking-wider section-label">
                            {item.category}
                          </p>
                        </div>
                        <ChevronIcon open={isOpen} />
                      </button>
                      {isOpen && (
                        <div
                          className="px-4 pb-4 text-sm leading-relaxed"
                          style={{ color: "var(--text-muted)" }}
                        >
                          {item.content()}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          /* Categorized sections */
          <div className="space-y-8">
            {faqSections.map((section) => (
              <div key={section.category}>
                <p className="section-label mb-3">{section.category}</p>
                <div
                  className="rounded-[10px] border overflow-hidden"
                  style={{ borderColor: "var(--border-subtle)" }}
                >
                  {section.items.map((item, i) => {
                    const key = `${section.category}-${i}`;
                    const isOpen = openIndex === key;
                    return (
                      <div
                        key={key}
                        style={{
                          borderBottom:
                            i < section.items.length - 1
                              ? "1px solid var(--border-subtle)"
                              : "none",
                        }}
                      >
                        <button
                          type="button"
                          className="flex w-full cursor-pointer items-center justify-between px-4 py-3 text-left"
                          onClick={() => toggle(key)}
                          aria-expanded={isOpen}
                        >
                          <p
                            className="text-sm font-medium"
                            style={{ color: "var(--text)" }}
                          >
                            {item.title()}
                          </p>
                          <ChevronIcon open={isOpen} />
                        </button>
                        {isOpen && (
                          <div
                            className="px-4 pb-4 text-sm leading-relaxed"
                            style={{ color: "var(--text-muted)" }}
                          >
                            {item.content()}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer CTA */}
      <div
        className="mt-10 border-t pt-6"
        style={{ borderColor: "var(--border-subtle)" }}
      >
        <p className="text-sm" style={{ color: "var(--text-muted)" }}>
          {m.help_still_need_help()}
        </p>
        <Link
          to="/feedback"
          className="mt-1.5 inline-block text-sm font-medium"
          style={{ color: "var(--accent)" }}
        >
          {m.nav_feedback()} →
        </Link>
      </div>
    </div>
  );
}
